import { useContext } from "react";
import { Box, TextField, Checkbox, styled } from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import FormControlLabel from '@mui/material/FormControlLabel';
import { GlobalButton, GlobalSecondaryButton } from "../../../styles";
import { LocationContext } from "../../../../context/LocationContext";
import AuditInfoLabel from "../../../../components/AuditInfoLabel";

const StyledBox = styled(Box)(() => ({
  display: 'flex',
  gap: "25px",
  justifyContent: "normal!important"
}));

const StyledTextField = styled(TextField)(() => ({
  width: "30rem"
}));

const LocationForm = ({ locationData, siteData, onSave, onCancel}) => {

  const { locations } = useContext(LocationContext);

  const isAddLocation = !locationData;

  const getInvalidLocationNames = () => {
    const allContextLocationNames = Array.isArray(locations)
      ? locations.map(x => x.locationName)
      : [];
    const newLocationNames = Array.isArray(siteData?.locations)
      ? siteData.locations.filter(x => x.isNew).map(x => x.locationName)
      : [];
    return [...allContextLocationNames, ...newLocationNames];
  };

  const invalidLocationNames = isAddLocation ?  getInvalidLocationNames() : [];

  const validationSchema = Yup.object({
    locationName: Yup.string()
      .transform((value) => (value ? value.trim() : ''))
      .required('Location Name is required')
      .max(70, 'Must be 70 characters or less')
      .test("delivery-no-whitespace", "Delivery Location Name cannot contain whitespace", function () {
        return !this.parent.isDeliveryLocation || !/\s/.test(this.originalValue || '');
      })
      .test("unique-name", "Location Name must be unique", (value) => {
        if (!value) return true;
        const lowerCaseValue = value.toLowerCase().trim();
        return !invalidLocationNames.some((name) => name.toLowerCase().trim() === lowerCaseValue);
      })
  });

  const initialValues = {
    locationName: locationData?.locationName ?? '',
    siteName: locationData?.siteName ?? siteData?.siteName,
    isActive: locationData?.isActive ?? true,
    canContainSamples: locationData?.canContainSamples ?? true,
    isDeliveryLocation: locationData?.isDeliveryLocation ?? false,
    isNew: locationData?.isNew ?? true,
    isModified: true,
    createdByEmail: locationData?.createdByEmail,
    createdDate: locationData?.createdDate,
    lastModifiedByEmail: locationData?.lastModifiedByEmail,
    lastModifiedDate: locationData?.lastModifiedDate
  };
  

  const handleCancelClick = () => onCancel?.();  
  const handleSubmit = (values) => onSave?.(values, isAddLocation);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, handleChange, handleBlur, errors, touched, isValid, dirty }) => (
        <Form style={{marginTop:"10px"}}>
          <StyledBox sx={{flexDirection:"column"}}>

            <StyledTextField
              name="locationName"
              fullWidth
              variant="outlined" size="small"
              disabled={!isAddLocation}
              label="Location Name"
              value={values.locationName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={(touched.locationName || values.isDeliveryLocation) && Boolean(errors.locationName)}
              helperText={(touched.locationName || values.isDeliveryLocation) && errors.locationName}
            />

            <StyledTextField
              fullWidth
              label="Site Name"
              name="siteName"
              variant="outlined" size="small"
              value={values.siteName}
              onChange={handleChange}
              disabled
            />

            <StyledBox sx={{ flexDirection: 'row' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isActive"
                    checked={values.isActive}
                    onChange={handleChange}
                  />
                }
                label="Active"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    name="canContainSamples"
                    checked={values.canContainSamples}
                    onChange={handleChange}
                  />
                }
                label="Contain Samples"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    name="isDeliveryLocation"
                    checked={values.isDeliveryLocation}
                    onChange={handleChange}
                  />
                }
                label="Delivery Location"
              />
            </StyledBox>

            <AuditInfoLabel auditObject={values} />

            <div style={{ display: "flex", justifyContent: "center" }}>
              <GlobalButton
                type="submit"
                variant="contained"
                disabled={!isValid || !dirty }>
                {isAddLocation ? "Add" : "Update"}
              </GlobalButton>

              <GlobalSecondaryButton
                variant="contained"
                style={{ marginLeft: "40px" }}
                onClick={() => {
                  handleCancelClick();
                }}>
                Cancel
              </GlobalSecondaryButton>
            </div>

          </StyledBox>
        </Form>
      )}
    </Formik>
  );
};

export default LocationForm;
